import { alpha } from '@mui/material/styles';
import { THEME_COLORS } from '../shared/constants/theme';
import { DASHBOARD_ACCENT, getDashboardDarkSurfaceTokens } from './dashboardSurfaceSx';

export { DASHBOARD_ACCENT };

export const HOTELIER_HERO_HEIGHT = 200;

export function getHotelierSurfaceTokens(isDark) {
  return getDashboardDarkSurfaceTokens(isDark);
}

export function getHotelierCardSx(isDark, options = {}) {
  const { disabled = false } = options;
  const tokens = getHotelierSurfaceTokens(isDark);

  return {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    cursor: disabled ? 'not-allowed' : 'pointer',
    position: 'relative',
    transition: 'transform 0.32s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.32s ease, border-color 0.32s ease',
    border: `1px solid ${tokens.surfaceBorder}`,
    borderRadius: '20px',
    background: isDark
      ? `linear-gradient(165deg, ${alpha('#ffffff', 0.04)} 0%, ${tokens.surface} 38%, ${THEME_COLORS.BACKGROUND_DARK} 100%)`
      : 'linear-gradient(165deg, #ffffff 0%, #f8fafc 100%)',
    overflow: 'hidden',
    opacity: disabled ? 0.62 : 1,
    boxShadow: isDark
      ? `inset 0 1px 0 ${alpha('#ffffff', 0.06)}, 0 12px 32px ${alpha('#000', 0.35)}`
      : `0 10px 28px ${alpha('#0f172a', 0.08)}`,
    ...(disabled ? { filter: 'grayscale(24%)' } : {}),
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: 'inherit',
      pointerEvents: 'none',
      boxShadow: `inset 0 0 0 1px ${alpha(DASHBOARD_ACCENT, 0)}`,
      transition: 'box-shadow 0.32s ease',
    },
    ...(!disabled
      ? {
          '&:hover': {
            transform: 'translateY(-8px)',
            borderColor: alpha(DASHBOARD_ACCENT, isDark ? 0.38 : 0.28),
            boxShadow: isDark
              ? `0 22px 48px ${alpha('#000', 0.45)}, 0 0 0 1px ${alpha(DASHBOARD_ACCENT, 0.2)}, 0 0 40px ${alpha(DASHBOARD_ACCENT, 0.08)}`
              : `0 20px 40px ${alpha(DASHBOARD_ACCENT, 0.18)}, 0 0 0 1px ${alpha(DASHBOARD_ACCENT, 0.12)}`,
            '&::after': {
              boxShadow: `inset 0 0 0 1px ${alpha(DASHBOARD_ACCENT, 0.22)}`,
            },
            '& .hotelier-hero-img': { transform: 'scale(1.06)' },
            '& .hotelier-hero-overlay': { opacity: 1 },
            '& .hotelier-open-row': { opacity: 1, transform: 'translateY(0)' },
            '& .hotelier-floating-icon': {
              transform: 'translateY(-2px) scale(1.04)',
              boxShadow: `0 12px 28px ${alpha(DASHBOARD_ACCENT, 0.28)}`,
            },
          },
        }
      : {}),
  };
}

export function getHotelierHeroOverlaySx(isDark) {
  return {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    opacity: 0.92,
    transition: 'opacity 0.32s ease',
    background: isDark
      ? `linear-gradient(180deg, ${alpha('#000', 0.08)} 0%, ${alpha('#000', 0.02)} 42%, ${alpha(THEME_COLORS.BACKGROUND_DARK, 0.92)} 100%)`
      : `linear-gradient(180deg, ${alpha('#000', 0.04)} 0%, transparent 40%, ${alpha('#fff', 0.96)} 100%)`,
  };
}

export function getHotelierImagePlaceholderSx(isDark) {
  const tokens = getHotelierSurfaceTokens(isDark);
  return {
    height: HOTELIER_HERO_HEIGHT,
    minHeight: HOTELIER_HERO_HEIGHT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: isDark
      ? `radial-gradient(ellipse 80% 70% at 50% 20%, ${alpha(DASHBOARD_ACCENT, 0.14)} 0%, transparent 55%), linear-gradient(160deg, ${THEME_COLORS.SURFACE_HEADER_DARK} 0%, ${THEME_COLORS.BACKGROUND_DARK} 100%)`
      : `radial-gradient(ellipse 80% 70% at 50% 20%, ${alpha(DASHBOARD_ACCENT, 0.1)} 0%, transparent 55%), linear-gradient(160deg, #f8fafc 0%, #eef2f7 100%)`,
    borderBottom: `1px solid ${tokens.surfaceBorder}`,
  };
}

export function getHotelierFloatingIconSx(isDark) {
  return {
    position: 'absolute',
    top: -26,
    left: 20,
    width: 52,
    height: 52,
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: DASHBOARD_ACCENT,
    background: isDark
      ? `linear-gradient(145deg, ${alpha(DASHBOARD_ACCENT, 0.28)} 0%, ${THEME_COLORS.SURFACE_HEADER_DARK} 100%)`
      : `linear-gradient(145deg, ${alpha(DASHBOARD_ACCENT, 0.16)} 0%, #ffffff 100%)`,
    border: `1px solid ${alpha(DASHBOARD_ACCENT, isDark ? 0.35 : 0.25)}`,
    boxShadow: isDark
      ? `0 10px 24px ${alpha('#000', 0.4)}, inset 0 1px 0 ${alpha('#fff', 0.08)}`
      : `0 8px 20px ${alpha(DASHBOARD_ACCENT, 0.15)}`,
    transition: 'transform 0.32s ease, box-shadow 0.32s ease',
    zIndex: 2,
  };
}

export function getHotelierStatusChipSx(status) {
  const glass = {
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
  };

  if (status === 'active') {
    return {
      ...glass,
      height: 28,
      fontSize: '0.68rem',
      fontWeight: 800,
      borderRadius: '999px',
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      bgcolor: alpha(DASHBOARD_ACCENT, 0.92),
      color: '#04120e',
      boxShadow: `0 8px 20px ${alpha(DASHBOARD_ACCENT, 0.35)}`,
      border: `1px solid ${alpha('#fff', 0.2)}`,
      '& .MuiChip-label': { px: 1.35 },
    };
  }
  if (status === 'onboarding') {
    return {
      ...glass,
      height: 28,
      fontSize: '0.68rem',
      fontWeight: 800,
      borderRadius: '999px',
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      bgcolor: alpha('#f6da6e', 0.22),
      color: '#f6da6e',
      border: `1px solid ${alpha('#f6da6e', 0.35)}`,
      '& .MuiChip-label': { px: 1.35 },
    };
  }
  return {
    ...glass,
    height: 28,
    fontSize: '0.68rem',
    fontWeight: 800,
    borderRadius: '999px',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    bgcolor: alpha('#f87171', 0.2),
    color: '#fecaca',
    border: `1px solid ${alpha('#f87171', 0.35)}`,
    '& .MuiChip-label': { px: 1.35 },
  };
}

export function getHotelierMetaChipSx(isDark) {
  const tokens = getHotelierSurfaceTokens(isDark);
  return {
    height: 28,
    fontSize: '0.72rem',
    fontWeight: 700,
    borderRadius: '999px',
    bgcolor: isDark ? alpha('#ffffff', 0.05) : alpha('#000', 0.03),
    color: tokens.text,
    border: `1px solid ${tokens.surfaceBorder}`,
    '& .MuiChip-label': { px: 1.35 },
  };
}

export function getHotelierRatingPillSx(isDark) {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.55,
    px: 1.35,
    py: 0.5,
    borderRadius: '999px',
    bgcolor: alpha('#f6da6e', isDark ? 0.14 : 0.12),
    border: `1px solid ${alpha('#f6da6e', isDark ? 0.28 : 0.22)}`,
    color: '#f6da6e',
    fontSize: '0.72rem',
    fontWeight: 800,
    letterSpacing: '0.02em',
  };
}

export function getHotelierLocationBarSx(isDark) {
  const tokens = getHotelierSurfaceTokens(isDark);
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 1.15,
    mt: 1.75,
    p: 1.15,
    pr: 1.35,
    borderRadius: '14px',
    bgcolor: isDark ? alpha('#ffffff', 0.04) : alpha(DASHBOARD_ACCENT, 0.04),
    border: `1px solid ${alpha(DASHBOARD_ACCENT, isDark ? 0.14 : 0.12)}`,
    transition: 'background-color 0.28s ease, border-color 0.28s ease',
  };
}

export function getHotelierLocationIconSx(isDark) {
  return {
    width: 34,
    height: 34,
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: DASHBOARD_ACCENT,
    bgcolor: alpha(DASHBOARD_ACCENT, isDark ? 0.14 : 0.1),
    border: `1px solid ${alpha(DASHBOARD_ACCENT, 0.2)}`,
  };
}

export function getHotelierOpenRowSx(isDark) {
  const tokens = getHotelierSurfaceTokens(isDark);
  return {
    mt: 1.75,
    pt: 1.5,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderTop: `1px dashed ${alpha(tokens.surfaceBorder, 0.85)}`,
    opacity: 0.55,
    transform: 'translateY(4px)',
    transition: 'opacity 0.28s ease, transform 0.28s ease',
    color: DASHBOARD_ACCENT,
  };
}

export function getHotelierCardContentSx() {
  return {
    flexGrow: 1,
    position: 'relative',
    pt: 3.5,
    px: 2.25,
    pb: 2.25,
    '&:last-child': { pb: 2.25 },
  };
}

export function getHotelierEmptyPanelSx(isDark) {
  const tokens = getHotelierSurfaceTokens(isDark);
  return {
    p: 3,
    borderRadius: '16px',
    background: isDark ? tokens.surfaceGradient : '#ffffff',
    border: `1px solid ${tokens.surfaceBorder}`,
    boxShadow: isDark ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : 'none',
  };
}

export function getHotelierRenewLinkSx(isDark) {
  return {
    mt: 1,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.6,
    px: 1.15,
    py: 0.5,
    borderRadius: '10px',
    textDecoration: 'none',
    fontSize: '0.68rem',
    fontWeight: 700,
    letterSpacing: '0.03em',
    border: `1px solid ${alpha('#f6da6e', 0.35)}`,
    backgroundColor: alpha('#f6da6e', isDark ? 0.12 : 0.14),
    color: '#f6da6e',
    '&:hover': {
      backgroundColor: alpha('#f6da6e', isDark ? 0.18 : 0.2),
      textDecoration: 'none',
    },
  };
}

/** @deprecated Use getHotelierFloatingIconSx */
export function getHotelierIconBoxSx(isDark) {
  return getHotelierFloatingIconSx(isDark);
}
